import { HttpBackend, HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { ApiService } from './api.service';
import { BrowserFingerprintService } from './browser-fingerprint.service';

/** Cookie session flag + cold-start probe (same URL as menus; bypasses interceptors). */
@Injectable({ providedIn: 'root' })
export class AuthSessionService {
  private static readonly signLinkScreenIdKey = 'signLinkScreenId';
  private sessionActive = false;
  private readonly forcedPasswordChangeKey = 'forcedPasswordChange';
  private readonly forcedPasswordChangeUserKey = 'forcedPasswordChangeUser';
  private readonly rawHttp: HttpClient;
  private forcedSessionValidation: Promise<boolean> | null = null;

  constructor(
    backend: HttpBackend,
    private apiService: ApiService,
    private fingerprintService: BrowserFingerprintService,
    private router: Router,
  ) {
    this.rawHttp = new HttpClient(backend);
  }

  isSessionActive(): boolean {
    return this.sessionActive;
  }

  /** True when the app has an active cookie session and a stored current user. */
  hasActiveAuthenticatedSession(): boolean {
    if (sessionStorage.getItem('forcedLogoutByExtension') === '1') {
      return false;
    }
    if (localStorage.getItem('authSessionActive') !== '1') {
      return false;
    }
    return this.sessionActive && !!localStorage.getItem('currentUser');
  }

  /** Mandatory password change (first-time / expired / status new) is pending. */
  isForcedPasswordChange(): boolean {
    try {
      return sessionStorage.getItem(this.forcedPasswordChangeKey) === '1';
    } catch {
      return false;
    }
  }

  setForcedPasswordChange(userName?: string): void {
    try {
      sessionStorage.setItem(this.forcedPasswordChangeKey, '1');
      const name = (userName || '').trim();
      if (name) {
        sessionStorage.setItem(this.forcedPasswordChangeUserKey, name);
      }
    } catch {
      /* ignore */
    }
  }

  clearForcedPasswordChange(): void {
    try {
      sessionStorage.removeItem(this.forcedPasswordChangeKey);
      sessionStorage.removeItem(this.forcedPasswordChangeUserKey);
    } catch {
      /* ignore */
    }
  }

  /**
   * Confirms the forced-change cookie session is still valid.
   * Clears the forced flag (and local session markers) when the probe fails.
   */
  async validateForcedPasswordChangeSession(): Promise<boolean> {
    if (!this.isForcedPasswordChange()) {
      return false;
    }
    if (this.forcedSessionValidation) {
      return this.forcedSessionValidation;
    }
    this.forcedSessionValidation = this.runForcedSessionValidation().finally(() => {
      this.forcedSessionValidation = null;
    });
    return this.forcedSessionValidation;
  }

  private async runForcedSessionValidation(): Promise<boolean> {
    try {
      await this.probeSession();
      if (this.hasActiveAuthenticatedSession()) {
        return true;
      }
    } catch {
      /* fall through */
    }
    this.invalidateForcedPasswordChangeSession();
    return false;
  }

  /** Clears forced-change state and local auth markers when the cookie/session is gone. */
  invalidateForcedPasswordChangeSession(): void {
    this.clearForcedPasswordChange();
    try {
      localStorage.removeItem('currentUser');
      localStorage.removeItem('menuItems');
      localStorage.removeItem('role');
      localStorage.removeItem('isIndividualUser');
    } catch {
      /* ignore */
    }
    this.markLoggedOut();
  }

  getForcedPasswordChangeUserName(): string {
    try {
      return (
        sessionStorage.getItem(this.forcedPasswordChangeUserKey) ||
        this.getCurrentUserName()
      );
    } catch {
      return this.getCurrentUserName();
    }
  }

  getCurrentUserName(): string {
    try {
      const raw = localStorage.getItem('currentUser');
      if (!raw) {
        return '';
      }
      const user = JSON.parse(raw);
      return String(user?.user_name || '').trim();
    } catch {
      return '';
    }
  }

  /** UrlTree query params used when redirecting back into the forced flow. */
  forcedPasswordChangeQueryParams(): { userName?: string } {
    const userName = this.getForcedPasswordChangeUserName();
    return userName ? { userName } : {};
  }

  /** Manual-sign / QR popup opened via window.open from the parent tab. */
  static isSecondScreenChild(): boolean {
    return typeof window !== 'undefined' && !!window.opener;
  }

  /** Top-level guest sign-link (`#/digital-signature?ref=…`) — not bare path, dashboard child, or -manual. */
  static isGuestSignLinkBoot(): boolean {
    if (typeof window === 'undefined') {
      return false;
    }
    const hash = window.location.hash || '';
    // Require non-empty ref (same gate as digital-signature isSignerUser).
    if (!/^#\/digital-signature\?/.test(hash)) {
      return false;
    }
    const query = hash.slice(hash.indexOf('?') + 1);
    const ref = new URLSearchParams(query).get('ref');
    return !!ref;
  }

  markLoggedIn(): void {
    this.sessionActive = true;
    try {
      localStorage.setItem('authSessionActive', '1');
    } catch {
      /* ignore */
    }
  }

  markLoggedOut(): void {
    this.sessionActive = false;
    this.clearForcedPasswordChange();
    try {
      localStorage.removeItem('authSessionActive');
    } catch {
      /* ignore */
    }
  }

  /** Tab-scoped copy of the sign-link screen id; another tab's logout clears localStorage. */
  static rememberSignLinkScreenId(screenId: string): void {
    try {
      sessionStorage.setItem(AuthSessionService.signLinkScreenIdKey, String(screenId));
    } catch {
      /* ignore */
    }
  }

  /** Puts `screenId` back on a sign-link tab after another tab's logout wiped localStorage. */
  static restoreSignLinkScreenId(): void {
    try {
      const screenId = sessionStorage.getItem(AuthSessionService.signLinkScreenIdKey);
      if (screenId && localStorage.getItem('screenId') !== screenId) {
        localStorage.setItem('screenId', screenId);
      }
    } catch {
      /* ignore */
    }
  }

  /** True when an authenticated user should be redirected away from /login. */
  shouldBlockGuestRoute(): boolean {
    if (sessionStorage.getItem('forcedLogoutByExtension') === '1') {
      return false;
    }
    if (localStorage.getItem('authSessionActive') !== '1') {
      this.markLoggedOut();
    }
    const hasCurrentUser = !!localStorage.getItem('currentUser');
    return this.sessionActive && hasCurrentUser;
  }

  /** Extension: exchange one-time Bearer token for access/refresh cookies. */
  async exchangeExtensionToken(token: string): Promise<void> {
    const url = this.apiService.getAuthHandoffUrl();
    const headers = this.buildHandoffHeaders(token);
    await firstValueFrom(
      this.rawHttp.post(url, {}, { withCredentials: true, headers }),
    );
    this.markLoggedIn();
  }

  private buildHandoffHeaders(token: string): HttpHeaders {
    let headers = new HttpHeaders({
      Accept: 'application/json',
      Authorization: `Bearer ${token}`,
    });
    const fp = this.fingerprintService.getCached();
    if (fp) {
      headers = headers.set('X-Device-Fingerprint', fp);
    }
    return headers.set('Accept-Language', localStorage.getItem('language') || 'en');
  }

  private buildProbeHeaders(): HttpHeaders {
    let headers = new HttpHeaders({ Accept: 'application/json' });
    const fp = this.fingerprintService.getCached();
    if (fp) {
      headers = headers.set('X-Device-Fingerprint', fp);
    }
    headers = headers.set('Accept-Language', localStorage.getItem('language') || 'en');
    const removeScreenId = localStorage.getItem('removeScreenId') === 'true';
    const screenId = localStorage.getItem('screenId');
    if (this.router.url.includes('/digital-signature?')) {
      if (screenId) {
        headers = headers.set('screen_id', screenId);
      }
    } else if (!removeScreenId && screenId) {
      headers = headers.set('screen_id', screenId);
    }
    localStorage.removeItem('removeScreenId');
    return headers;
  }

  async probeSession(): Promise<void> {
    if (AuthSessionService.isSecondScreenChild()) {
      if (
        localStorage.getItem('authSessionActive') === '1' ||
        localStorage.getItem('currentUser')
      ) {
        this.sessionActive = true;
      }
      return;
    }
    const url = this.apiService.getReturnRoleByUserNameUrl();
    try {
      const body = await firstValueFrom(
        this.rawHttp.get(url, { withCredentials: true, headers: this.buildProbeHeaders() }),
      );
      if (this.isSessionInvalidProbeBody(body)) {
        this.markLoggedOut();
        return;
      }
      this.markLoggedIn();
    } catch {
      this.markLoggedOut();
    }
  }

  private isSessionInvalidProbeBody(body: unknown): boolean {
    if (!body || typeof body !== 'object' || !('errorCode' in body)) {
      return false;
    }
    const errorCode = (body as { errorCode?: unknown }).errorCode;
    const codes = Array.isArray(errorCode)
      ? errorCode.map((value) => String(value).toUpperCase())
      : [String(errorCode ?? '').toUpperCase()];
    return codes.some(
      (code) =>
        code === 'EMPTY TOKEN' ||
        code === 'INVALID TOKEN' ||
        code === 'TOKEN EXPIRED' ||
        code === 'EXPIRED TOKEN' ||
        code === 'UNAUTHORIZED' ||
        code === '600',
    );
  }
}
